// Assessment data-access: quizzes, attempts, assignments, submissions.
import { supabase } from '../integrations/supabase/client';
import type { Quiz, QuizAttempt, Assignment } from '../types/assess';

export async function getQuizzesForCourse(courseId: string): Promise<Quiz[]> {
  const { data } = await supabase.from('quizzes').select('*').eq('course_id', courseId).order('position');
  const quizzes = ((data ?? []) as unknown as Quiz[]);
  for (const q of quizzes) {
    const { data: qs } = await supabase.from('quiz_questions').select('*').eq('quiz_id', q.id).order('position');
    q.questions = ((qs ?? []) as unknown as Quiz['questions']);
  }
  return quizzes;
}

export async function getAttempts(userId: string, quizIds: string[]): Promise<QuizAttempt[]> {
  if (quizIds.length === 0) return [];
  const { data } = await supabase.from('quiz_attempts').select('*').eq('user_id', userId).in('quiz_id', quizIds).order('created_at', { ascending: false });
  return ((data ?? []) as unknown as QuizAttempt[]);
}

export async function submitQuizAttempt(userId: string, quizId: string, answers: number[], questions: { correct_index: number }[], passMark: number) {
  const total = questions.length;
  let score = 0;
  answers.forEach((a, i) => { if (questions[i] && a === questions[i].correct_index) score++; });
  const passed = total === 0 ? false : Math.round((score / total) * 100) >= passMark;
  const { data, error } = await supabase.from('quiz_attempts').insert({ user_id: userId, quiz_id: quizId, score, total, passed, answers }).select().single();
  if (error) throw new Error(error.message);
  return data;
}

export async function getAssignmentsForCourse(courseId: string, userId?: string): Promise<Assignment[]> {
  const { data } = await supabase.from('assignments').select('*').eq('course_id', courseId).order('position');
  const list = ((data ?? []) as unknown as Assignment[]);
  if (userId) {
    for (const a of list) {
      const { data: sub } = await supabase.from('assignment_submissions').select('*').eq('assignment_id', a.id).eq('user_id', userId).maybeSingle();
      a.submission = (sub ?? null) as Assignment['submission'];
    }
  }
  return list;
}

export async function submitAssignment(userId: string, assignmentId: string, content: string, fileUrl?: string | null) {
  const { error } = await supabase.from('assignment_submissions').upsert(
    { user_id: userId, assignment_id: assignmentId, content, file_url: fileUrl ?? null, status: 'submitted' },
    { onConflict: 'user_id,assignment_id' }
  );
  if (error) throw new Error(error.message);
}

// ---- admin ----
export async function createQuizAdmin(courseId: string, title: string, passMark = 70) {
  const { data, error } = await supabase.from('quizzes').insert({ course_id: courseId, title, pass_mark: passMark }).select().single();
  if (error) throw new Error(error.message);
  return data;
}

export async function deleteQuizAdmin(quizId: string) {
  const { error } = await supabase.from('quizzes').delete().eq('id', quizId);
  if (error) throw new Error(error.message);
}

export async function addQuestionAdmin(quizId: string, question: string, options: string[], correctIndex: number) {
  const { error } = await supabase.from('quiz_questions').insert({ quiz_id: quizId, question, options, correct_index: correctIndex });
  if (error) throw new Error(error.message);
}

export async function deleteQuestionAdmin(questionId: string) {
  const { error } = await supabase.from('quiz_questions').delete().eq('id', questionId);
  if (error) throw new Error(error.message);
}

export async function createAssignmentAdmin(courseId: string, title: string, description = '') {
  const { error } = await supabase.from('assignments').insert({ course_id: courseId, title, description });
  if (error) throw new Error(error.message);
}

export async function deleteAssignmentAdmin(assignmentId: string) {
  const { error } = await supabase.from('assignments').delete().eq('id', assignmentId);
  if (error) throw new Error(error.message);
}
